import { createDynamicFeatureSelector } from '@app/app.selectors';
import { FieldKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { GroupedNameEmailAddressAndPersonsByTariffUuids } from '@core/data-access/models/form/form-validity.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { PersonType, PersonUuid } from '@core/data-access/models/person.model';
import {
  TicketAssignmentDynamicFields,
  TicketAssignmentStoreState,
  TicketHolderForm,
  TicketHolderForms,
  TicketHolderQuestionnaireDynamicFields,
  TicketHolders
} from '@core/features/ticket-assignment/models/ticket-assignment-store.model';
import {
  initialTicketAssignmentStoreState,
  ticketAssignmentReducerKey
} from '@core/features/ticket-assignment/store/ticket-assignment.reducer';
import { createSelector } from '@ngrx/store';
import { TariffUuid, TicketPersonId } from '@products/models/tariff.model';
import { defaultTo, isEmpty, isNil, keys, pickBy, reduce } from 'lodash';

const ticketAssignmentFeatureSelector = createDynamicFeatureSelector<TicketAssignmentStoreState>(
  ticketAssignmentReducerKey,
  initialTicketAssignmentStoreState
);

export const selectSelectedTariffUuid = createSelector(
  ticketAssignmentFeatureSelector,
  (ticketAssignment): TariffUuid => ticketAssignment?.selectedTariffUuid
);

export const selectTicketAssignmentTicketHolders = createSelector(
  ticketAssignmentFeatureSelector,
  (ticketAssignment): TicketHolders => ticketAssignment?.ticketHolders
);

export const selectTicketAssignmentTicketHolderByPersonUuid = (personUuid: PersonUuid) =>
  createSelector(selectTicketAssignmentTicketHolders, (ticketHolders) => ticketHolders[personUuid]);

export const selectTicketAssignmentDynamicFields = createSelector(
  ticketAssignmentFeatureSelector,
  (ticketAssignment): TicketAssignmentDynamicFields => ticketAssignment?.dynamicFields
);

export const selectTicketHolderQuestionnaireDynamicFields = createSelector(
  selectTicketAssignmentDynamicFields,
  (ticketAssignmentDynamicFields): TicketHolderQuestionnaireDynamicFields =>
    ticketAssignmentDynamicFields[FormName.TicketHolderQuestionnaire]
);

export const selectTicketHolderQuestionnaireDynamicFieldsByTicketPersonId = (ticketPersonId: TicketPersonId) =>
  createSelector(
    selectTicketHolderQuestionnaireDynamicFields,
    (ticketHolderQuestionnaireDynamicFields) => ticketHolderQuestionnaireDynamicFields[ticketPersonId]
  );

export const selectTicketAssignmentForms = createSelector(
  ticketAssignmentFeatureSelector,
  (ticketAssignment) => ticketAssignment?.forms
);

export const selectTicketHolderForms = createSelector(
  selectTicketAssignmentForms,
  (ticketAssignmentForms): TicketHolderForms => ticketAssignmentForms[FormName.TicketHolder]
);

export const selectTicketHolderBuyerForms = createSelector(selectTicketHolderForms, (ticketHolderForms) =>
  pickBy(ticketHolderForms, ({ person }) => !isNil(person) && person.type === PersonType.BuyerIsHolder)
);

export const selectTicketHolderFormTariffUuids = createSelector(selectTicketHolderForms, (ticketHolderForms) =>
  keys(ticketHolderForms)
);

export const selectTicketHolderFormsByPersonUuid = (personUuid: PersonUuid) =>
  createSelector(selectTicketHolderForms, (ticketHolderForms) =>
    pickBy(ticketHolderForms, ({ person }) => !isNil(person) && person.uuid === personUuid)
  );

export const selectTicketHolderFormsTariffUuidsByPersonUuid = (personUuid: PersonUuid) =>
  createSelector(selectTicketHolderFormsByPersonUuid(personUuid), keys);

export const selectSelectedTariffUuidTicketHolderForm = createSelector(
  ticketAssignmentFeatureSelector,
  ({ selectedTariffUuid, forms }) => forms.ticketHolder[selectedTariffUuid]
);

export const selectSelectedTariffHolderPerson = createSelector(
  ticketAssignmentFeatureSelector,
  ({ selectedTariffUuid, forms }) => forms.ticketHolder[selectedTariffUuid]?.person
);

export const selectGroupedNameEmailAddressAndPersonsByTariffUuids = createSelector(
  selectTicketHolderForms,
  (ticketHolderForms): GroupedNameEmailAddressAndPersonsByTariffUuids =>
    reduce(
      ticketHolderForms,
      (groupedNameAndEmailsByTariffUuid, ticketHolderForm, tariffUuid) => {
        const person = ticketHolderForm.person;
        const firstName = defaultTo(ticketHolderForm.formFields[FieldKey.FirstName]?.value as string, '');
        const lastName = defaultTo(ticketHolderForm.formFields[FieldKey.LastName]?.value as string, '');
        const name = !isEmpty(firstName) && !isEmpty(lastName) ? firstName + ' ' + lastName : '';
        const email = defaultTo(ticketHolderForm.formFields[FieldKey.Email]?.value, '');

        groupedNameAndEmailsByTariffUuid[tariffUuid] = {
          person,
          name,
          email
        };

        return groupedNameAndEmailsByTariffUuid;
      },
      {}
    )
);

export const selectSelectedTariffHolderUuid = createSelector(
  selectSelectedTariffHolderPerson,
  (person): PersonUuid | null => person?.uuid
);

export const selectTicketHolderFormByTariffUuid = (tariffUuid: TariffUuid) =>
  createSelector(selectTicketHolderForms, (ticketHolderForms): TicketHolderForm => ticketHolderForms[tariffUuid]);

export const selectTicketHolderFormFieldsByTariffUuid = (tariffUuid: TariffUuid) =>
  createSelector(selectTicketHolderFormByTariffUuid(tariffUuid), (ticketHolderForm) => ticketHolderForm?.formFields);

export const selectSelectedTariffUuidTicketHolderQuestionnaireForm = createSelector(
  ticketAssignmentFeatureSelector,
  ({ selectedTariffUuid, forms }) => forms.ticketHolderQuestionnaire[selectedTariffUuid]
);

export const selectTicketHolderQuestionnaireForms = createSelector(
  selectTicketAssignmentForms,
  ({ ticketHolderQuestionnaire }) => ticketHolderQuestionnaire
);

export const selectTicketHolderQuestionnaireFormsByPersonUuid = (personUuid: PersonUuid) =>
  createSelector(selectTicketHolderQuestionnaireForms, (ticketHolderQuestionnaireForms) =>
    pickBy(ticketHolderQuestionnaireForms, ({ person }) => !isNil(person) && person.uuid === personUuid)
  );

export const selectTicketHolderQuestionnaireFormsTariffUuidsByPersonUuid = (personUuid: PersonUuid) =>
  createSelector(selectTicketHolderQuestionnaireFormsByPersonUuid(personUuid), keys);

export const selectTicketHolderQuestionnaireFormTariffUuids = createSelector(
  selectTicketHolderQuestionnaireForms,
  (ticketHolderQuestionnaireForms) => keys(ticketHolderQuestionnaireForms)
);

export const selectTicketHolderQuestionnaireFormByTariffUuid = (tariffUuid: TariffUuid) =>
  createSelector(
    selectTicketHolderQuestionnaireForms,
    (ticketHolderQuestionnaireForms) => ticketHolderQuestionnaireForms[tariffUuid]
  );
